import type { ReactNode } from "react";
import type { Job } from "../../../models";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import type { NearbyJobInfo } from "../../SubmitJobs/Methods/Bulk/wizard/WizardContainer";
import type { MapFeatureData } from "../../SubmitJobs/Methods/Bulk/wizard/steps/OverlappingPoints/OverlappingPoints.types";

export interface BoundsPadding {
	top?: number;
	right?: number;
	bottom?: number;
	left?: number;
}

export type EditableJsonKeys = "boundaryGeoJSON" | "zonesGeoJSON" | "pointsGeoJSON";

export interface JobMapProps {
	// Data — provide one or the other
	jobs?: Job[];
	job?: Job; // Convenience: equivalent to jobs={[job]}

	// Display
	height?: string | number; // Accepts number (pixels).

	// Coloring:
	//   "layer-type": blue/green/red per layer type
	//   "per-job": hash-based color per job ID
	//   "boundary-hatch": boundary-only with filled polygons + hatch lines (wizard overlapping boundaries)
	//   "boundary-outline": boundary-only with polyline outlines + hatch lines (wizard duplicate boundaries)
	colorMode?: ColorMode;

	// Lasso selection (opt-in)
	lassoMode?: boolean;
	selectedFeatureIds?: Set<string>;
	onLassoComplete?: (selectedIds: Set<string>) => void;

	// Toolbar (opt-in) — when enabled, lasso state is managed internally
	showToolbar?: boolean;
	onClose?: () => void;
	toolbarActions?: ReactNode;

	// Feature selection callback (toolbar mode with lifted state)
	onSelectedFeaturesChange?: (ids: Set<string>) => void;

	// Edit (requires showToolbar) — editable is derived from onGeometryEdit being set
	onGeometryEdit?: (jobId: string, geoJSONKey: EditableJsonKeys, updated: FeatureCollection) => void;

	// Points visibility — undefined = always show
	zoomLevelToDisplayPoints?: number;

	// Map controls (kept for caller compat — mapped internally)
	disableDefaultUI?: boolean;
	zoomControl?: boolean;
	showEmptyState?: boolean;
	boundsPadding?: BoundsPadding;
	gestureHandling?: string; // "cooperative" → MapLibre cooperativeGestures

	// ── Wizard overlay props (only used by boundary-hatch / boundary-outline modes) ──

	/** Nearby jobs to display as grayed-out boundaries */
	nearbyJobs?: NearbyJobInfo[];
	/** Unmatched features (points/zones) to overlay on the map */
	unmatchedFeatures?: FeatureCollection;
	/** Duplicate features to overlay (colored point/zone markers) */
	duplicateFeatures?: MapFeatureData[];
	/** Highlight geometry (e.g. intersection area) rendered last on top */
	highlightGeometry?: Feature<Geometry>;

	// Visibility controls for wizard overlays
	hiddenJobIds?: string[];
	hiddenNearbyJobIds?: string[];
	hiddenDuplicateFeatureIds?: Set<string>;
	hideUnmatchedFeatures?: boolean;
}

export type ColorMode = "layer-type" | "per-job" | "boundary-hatch" | "boundary-outline";

export const DEFAULT_CENTER = { lng: -87.16533774444255, lat: 40.7671815905857 };

export const DEFAULT_ZOOM = 12;

export const LAYER_COLORS = {
	boundary: "#2196f3",
	zones: "#4caf50",
	points: "#ffffff",
} as const;

export const POLYGON_COLORS = [
	"#e51c23",
	"#e91e63",
	"#9c27b0",
	"#673ab7",
	"#3f51b5",
	"#5677fc",
	"#03a9f4",
	"#00bcd4",
	"#009688",
	"#259b24",
	"#8bc34a",
	"#afb42b",
	"#ff9800",
	"#ff5722",
	"#795548",
	"#607d8b",
	"#ffeb3b",
	"#cddc39",
	"#4caf50",
	"#8bc34a",
	"#cddc39",
	"#ffeb3b",
	"#ffc107",
	"#ff9800",
	"#ff5722",
	"#795548",
	"#9e9e9e",
	"#607d8b",
	"#3f51b5",
	"#2196f3",
	"#03a9f4",
	"#00bcd4",
	"#009688",
	"#4caf50",
	"#8bc34a",
	"#cddc39",
	"#ffeb3b",
	"#ffc107",
	"#ff9800",
	"#ff5722",
	"#795548",
	"#9e9e9e",
	"#607d8b",
];
